/**
 * DetailPanel - Right-side extending panel for tab content
 *
 * Slides in from the left when a tab is active.
 * Renders the corresponding tab content with a scrollable area.
 *
 * Tab header is conditionally shown:
 * - Aircraft tab: No header (uses internal block titles)
 * - Other tabs: Compact uppercase header
 */

import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { AircraftTab } from '../card-tabs/AircraftTab'
import { FinancialTab } from '../card-tabs/FinancialTab'
import { FuelTab } from '../card-tabs/FuelTab'
import { ClientTab } from '../card-tabs/ClientTab'
import { ScheduleTab } from '../card-tabs/ScheduleTab'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface DetailPanelProps {
  data: AllocationEnrichedData
}

// Tab display configuration
const TAB_DISPLAY: Record<string, { label: string; showHeader: boolean }> = {
  aircraft: { label: 'Aircraft', showHeader: false },
  financial: { label: 'Financial', showHeader: true },
  fuel: { label: 'Fuel', showHeader: true },
  client: { label: 'Client', showHeader: true },
  schedule: { label: 'Schedule', showHeader: true },
}

export function DetailPanel({ data }: DetailPanelProps) {
  const cardState = useAircraftCardStore((s) => s.cardState)

  if (cardState.mode !== 'expanded') return null

  const { activeTab } = cardState
  const tabConfig = TAB_DISPLAY[activeTab] || { label: activeTab, showHeader: true }

  return (
    <div className="w-[260px] border-l bg-muted/20 animate-in slide-in-from-right-2 duration-200">
      {/* Tab title - conditionally shown */}
      {tabConfig.showHeader && (
        <div className="px-3 pt-2.5 pb-1.5 border-b bg-muted/30">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/70">
            {tabConfig.label}
          </span>
        </div>
      )}

      {/* Tab content - scrollable */}
      <div className="px-3 py-2 max-h-[280px] overflow-y-auto">
        {activeTab === 'aircraft' && <AircraftTab data={data} />}
        {activeTab === 'financial' && <FinancialTab data={data} />}
        {activeTab === 'fuel' && <FuelTab data={data} />}
        {activeTab === 'client' && <ClientTab data={data} />}
        {activeTab === 'schedule' && <ScheduleTab data={data} />}
      </div>
    </div>
  )
}
